import { Suspense, useEffect, useState, ReactNode } from 'react';
import { Helmet as ReactHelmet } from 'react-helmet';
import { Switch, Route, useRouteMatch, Redirect } from 'react-router-dom';
import colorThis from '@eknowles/color-this';
import { ToastContainer, toast } from 'react-toastify';
import { ConnectedRouter } from 'connected-react-router';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { omit } from 'lodash';

import { fetchAuthStatus, getOrganization, loginViaApi, reloadMe } from 'actions/requests';

import { auth } from 'utils/firebase';
import { isUserThryveAdmin, userHasSpecialPermissionForAnyOrg } from 'utils/index';
import { ResponsePayload, SuperPermissions, AuthSuccessResult } from 'utils/gloo-types';
import InAppRoutes from 'utils/router';
import { useMe, useOrg } from 'utils/reduxHook';
import { setUser } from 'utils/analytics';

import Helmet from 'components/atoms/Helmet';
import Loader from 'components/molecules/Loader';
import ModalHandler, { ModalTypes } from 'components/organisms/ModalHandler';
import NavController from 'components/organisms/NavController';

import Login from 'pages/Login';
import { onAuthStateChanged, signInWithCustomToken, User } from 'firebase/auth';
import Cookies from 'js-cookie';
import AdminConversationPrompts from 'pages/_SuperAdminPages/AdminConversationPrompts';
import AdminHubAuthoring from 'pages/_AdminAuthoringPages/AdminHubAuthoring';
import { useIsMobile } from 'utils/hooks';
import { openModal } from 'actions/modal';
import AdminAuthoringHome from 'pages/_AdminAuthoringPages/AdminAuthoringHome';
import Home from 'pages/Home';
import AdminKitCreate from 'pages/_AdminAuthoringPages/AdminKitCreate';
import AdminKitEdit from 'pages/_AdminAuthoringPages/AdminKitEdit';
import AdminTemplateCreate from 'pages/_AdminAuthoringPages/AdminTemplateCreate';
import AdminTemplateEdit from 'pages/_AdminAuthoringPages/AdminTemplateEdit';
import { Configs, IS_DEV_ENV, IS_PROD_ENV, IS_STAGING_ENV, REDUX_ACTIONS } from './utils/constants';
import configureStore, { history } from './store';
import { RootState } from './reducers';
import { loginAction, logoutAction, loginWithoutAccountAction } from './actions/auth';
import Icon from './components/atoms/Icon';

const { REACT_APP_FIREBASE_COOKIE_BASE_NAME, REACT_APP_ENABLE_SSO } = process.env;

const { store, persistor } = configureStore();

function ThryveAdminGuard({ component, glooRouterAccess }: { component: ReactNode; glooRouterAccess?: boolean }): any {
  const orgUser = useMe();
  const isGlooRouter = userHasSpecialPermissionForAnyOrg(orgUser, [SuperPermissions.GLOO_CONNECT_ROUTER]);

  if (!isUserThryveAdmin(orgUser)) {
    if (glooRouterAccess && isGlooRouter) {
      // User has valid access and can continue
    } else {
      toast.error('Sorry, you do not have access to this.');
      return <Redirect to={InAppRoutes.Root} />;
    }
  }
  return component;
}

function NavControllerSwitch({ children }: { children: ReactNode }) {
  return <NavController>{children}</NavController>;
}

function AppRoutes() {
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const isLoggedInWithoutAccount = useSelector((state: RootState) => state.auth.isLoggedInWithoutAccount);
  const urlParams = new URLSearchParams(window.location.search);
  const popModal = urlParams.get('popModal');
  const dispatch = useDispatch();

  useEffect(() => {
    if (popModal) {
      let popModalProps = {};
      try {
        popModalProps = JSON.parse(urlParams.get('popModalProps') || '{}');
      } catch (error) {
        // error parsing json, do nothing
      }

      dispatch(openModal(popModal as ModalTypes, popModalProps));

      /**
       * This setTimeout is a hack to remove the popModal query param from the url
       * AFTER the page has a chance to redirect to the ported page with the
       * `popModal` params plugged in.
       * If it's NOT GOING TO REDIRECT, then we want to remove the query param
       */
      setTimeout(() => {
        if ((window.location.search || '').includes('popModal=')) {
          const urlParamsCopy = new URLSearchParams(window.location.search);
          urlParamsCopy.delete('popModal');
          urlParamsCopy.delete('popModalProps');
          history.replace({ search: urlParamsCopy.toString() });
        }
      }, 2000);
    }
  }, [popModal]);

  return (
    <>
      {!isLoggedIn && !isLoggedInWithoutAccount ? (
        <Switch>
          <Route exact path={InAppRoutes.Login}>
            <Login />
          </Route>
          <Route
            path={InAppRoutes.Root}
            render={({ location }) => {
              const queryParams = new URLSearchParams(location.search);
              if (![InAppRoutes.Login, InAppRoutes.Root, InAppRoutes.Home].includes(location.pathname)) {
                queryParams.set('redirect', location.pathname);
              }

              const searchStr = queryParams.toString().length > 0 ? `?${queryParams.toString()}` : '';
              return <Redirect to={InAppRoutes.Login + searchStr} />;
            }}
          />
        </Switch>
      ) : (
        <NavControllerSwitch>
          {/* <-- this is basically the main layout for the app */}
          <Switch>
            <Route exact path={InAppRoutes.Home}>
              <Home />
            </Route>
            <Route exact path={[InAppRoutes.AdminHubCreate, InAppRoutes.AdminHubEdit]}>
              <ThryveAdminGuard
                component={
                  <Suspense fallback={<Loader type="route" />}>
                    <AdminHubAuthoring />
                  </Suspense>
                }
              />
            </Route>
            <Route exact path={InAppRoutes.AdminAuthoringHome}>
              <ThryveAdminGuard component={<AdminAuthoringHome />} />
            </Route>
            <Route exact path={InAppRoutes.AdminKitEdit}>
              <ThryveAdminGuard
                component={
                  <Suspense fallback={<Loader type="route" />}>
                    <AdminKitEdit />
                  </Suspense>
                }
              />
            </Route>
            <Route exact path={InAppRoutes.AdminKitCreate}>
              <ThryveAdminGuard
                component={
                  <Suspense fallback={<Loader type="route" />}>
                    <AdminKitCreate />
                  </Suspense>
                }
              />
            </Route>
            <Route exact path={InAppRoutes.AdminTemplateEdit}>
              <ThryveAdminGuard
                component={
                  <Suspense fallback={<Loader type="route" />}>
                    <AdminTemplateEdit />
                  </Suspense>
                }
              />
            </Route>
            <Route exact path={InAppRoutes.AdminTemplateCreate}>
              <ThryveAdminGuard
                component={
                  <Suspense fallback={<Loader type="route" />}>
                    <AdminTemplateCreate />
                  </Suspense>
                }
              />
            </Route>
            <Route exact path={InAppRoutes.AdminConversationPrompts}>
              <ThryveAdminGuard glooRouterAccess component={<AdminConversationPrompts />} />
            </Route>
            <Route exact path={InAppRoutes.ConversationPromptCreate}>
              <ThryveAdminGuard glooRouterAccess component={<AdminConversationPrompts />} />
            </Route>
            <Route exact path={InAppRoutes.ConversationPromptEdit}>
              <ThryveAdminGuard glooRouterAccess component={<AdminConversationPrompts />} />
            </Route>
            <Route
              path={InAppRoutes.Root}
              render={({ location }) => {
                // If landing in home and we have a redirect param (from location.search or location.state),
                // navigate to there and remove the redirect param
                const searchParams = new URLSearchParams(location.search);
                const redirectParam = searchParams.get('redirect');
                const locationState = location.state as { redirect?: string };
                let redirectTo = redirectParam || locationState?.redirect;
                if (redirectTo) {
                  searchParams.delete('redirect');
                  const [newUrl, redirectSearchParams] = redirectTo.split('?');
                  if (newUrl !== redirectTo) {
                    redirectTo = newUrl;
                  }
                  return (
                    <Redirect
                      to={{
                        pathname: redirectTo,
                        search: redirectSearchParams
                          ? `?${redirectSearchParams}`
                          : searchParams.toString().length > 0
                            ? `?${searchParams.toString()}`
                            : undefined,
                        state: locationState?.redirect ? omit(locationState, 'redirect') : undefined,
                      }}
                    />
                  );
                }

                return <Redirect to={InAppRoutes.Home} />;
              }}
            />
            <Route>
              <div>Not Found Page</div>
            </Route>
          </Switch>
        </NavControllerSwitch>
      )}
    </>
  );
}

function ThryveAdminBanner() {
  const me = useMe();
  const organization = useOrg();
  const isMobile = useIsMobile();

  const routeMatch = useRouteMatch([InAppRoutes.Login]);
  if (!me || !isUserThryveAdmin(me) || routeMatch || isMobile) {
    return null;
  }
  const randomColor = colorThis(`${organization.name}`, 3);

  return (
    <span
      className="fixed z-50 text-center text-white bg-red px-4 rounded-r-full print:hidden"
      style={{ height: 30, left: 0, bottom: 0, backgroundColor: randomColor }}
    >
      <span className="font-bold text-xs">ADMIN: Accessing {organization.name}</span>
    </span>
  );
}

const toastType = {
  success: 'bg-green-100 border border-green-700 text-green-700 [&>button]:text-green-800',
  info: 'bg-blue-100 border border-blue-600 text-blue-600 [&>button]:text-blue-600',
  error: 'bg-red-100 border border-red-700 text-red-800 [&>button]:text-red-800',
  warning: 'bg-yellow-200 border border-yellow-900 text-yellow-900 [&>button]:text-yellow-900',
};

function MainApp() {
  return (
    <>
      <AppRoutes />
      <Helmet />
      <ToastContainer
        icon={({ type }) =>
          type === 'success' ? (
            <Icon name="toast-success-circle" />
          ) : type === 'error' ? (
            <Icon name="alert-triangle" />
          ) : type === 'info' ? (
            <Icon name="alert-triangle" />
          ) : type === 'warning' ? (
            <Icon name="alert-circle" />
          ) : undefined
        }
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        limit={3}
        bodyClassName="p-3 text-sm font-bold"
        toastClassName={({ type }) =>
          `relative w-full flex flex-row rounded-sm text-sm text-center mb-2 [&>button]:opacity-100 [&>button]:flex [&>button]:self-center [&>button]:pr-3 [&>button]:font-bold ${toastType[type]}`
        }
      />
      <ModalHandler />
      <ThryveAdminBanner />
    </>
  );
}

type LoggedOutCookie = 'yes' | 'no' | undefined;

function App() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Periodically check to see if another app has logged us out
    const interval = setInterval(async () => {
      const isLoggedOutCookie = Cookies.get(`${REACT_APP_FIREBASE_COOKIE_BASE_NAME}.LoggedOut`) as LoggedOutCookie;
      console.debug(`[tokenInterval] Checking value of logged out cookie: ${isLoggedOutCookie}`);
      if (store.getState().auth.isLoggedIn && isLoggedOutCookie === 'yes') {
        console.debug(`[tokenInterval] Cookie said yes, so forcing a log out.`);
        await store.dispatch(logoutAction() as any);
      }
    }, 1000 * 15); // Every 15 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    onAuthStateChanged(auth, async (user: User) => {
      if (REACT_APP_ENABLE_SSO === 'yes') {
        console.debug(`[tokenChangedHandler] SSO Enabled`);
        // This logic doesn't apply when accepting an invite
        const windowLocation = window.location.toString();
        if (windowLocation.includes(InAppRoutes.Invitation)) {
          console.debug(`[tokenChangedHandler] Exiting because we're accepting an invite`);
          setLoaded(true);
          return;
        }

        const isLoggedOutCookie = Cookies.get(`${REACT_APP_FIREBASE_COOKIE_BASE_NAME}.LoggedOut`) as LoggedOutCookie;
        let shouldLogoutUser = false;
        let accountExists = true;

        // If the user has the Gloo-Auth.LoggedOut cookie set to true, it means they logged themselves out in one of the apps
        // and we should log them out here too. This cookie is set to false when a user signs up or logs in
        if (isLoggedOutCookie === 'yes') shouldLogoutUser = true;

        /*
         * USER STATE #1
         *
         * We have a client side session with firebase, and the user hasn't been marked for logout.
         *
         * Attempt to set session cookies for the newly logged in user
         *
         */
        if (user && !shouldLogoutUser) {
          console.debug(
            `[tokenChangedHandler] USER STATE #1 FIRED with & authUser.id=${user.uid} & logoutUser=${shouldLogoutUser}`,
          );
          const userToken = await user.getIdToken();
          const response = await loginViaApi({ idToken: userToken, refreshToken: user.refreshToken });
          const responseJSON = (await response.json()) as ResponsePayload<AuthSuccessResult>;

          if (!response.ok) {
            throw new Error(
              `Received ${response.status} response from login API endpoint: ${JSON.stringify(responseJSON)}`,
            );
          }

          accountExists = responseJSON.data.accountExists;
        }

        /*
         * USER STATE #2
         *
         * We don't have a client side session, but the user has not logged out.
         *
         * Attempt to create a new firebase session using a custom token if the session cookie token
         * ID is valid
         *
         */
        if (!user && !shouldLogoutUser) {
          console.debug(
            `[tokenChangedHandler] USER STATE #2 FIRED with & authUser.id=${user?.uid} & logoutUser=${shouldLogoutUser}`,
          );
          // Try to auth  with server side session cookie containing their encrypted idToken
          // This encrypted cookie is shared between apps in gloo
          const response = await fetchAuthStatus();
          const responseJSON = (await response.json()) as ResponsePayload<AuthSuccessResult>;

          if (response.ok) {
            const {
              data: { idToken },
            } = responseJSON as ResponsePayload<{ idToken: string }>;
            try {
              await signInWithCustomToken(auth, idToken);
              accountExists = responseJSON.data.accountExists;
            } catch (error) {
              console.error(error);
              // token is invalid, someone is doing something shady or
              // somehow it expired, log em out!
              shouldLogoutUser = true;
            }
          } else {
            // we weren't able to check their status, or the validation of their
            // id token failed, so log em out!
            shouldLogoutUser = true;
          }
        }

        /*
         * USER STATE #3
         *
         * The user loggedOut somewhere
         *
         * Delete the session cookies, and sign out the firebase user
         *
         */
        if (shouldLogoutUser) {
          console.debug(
            `[tokenChangedHandler] USER STATE #3 FIRED with & authUser.id=${user?.uid} & logoutUser=${shouldLogoutUser}`,
          );
          await store.dispatch(logoutAction() as any);
        }

        // Dispatch the login action if we have a good user, with an account and we aren't going to log them out
        if (user && !shouldLogoutUser && accountExists) {
          await store.dispatch(loginAction(user) as any);
        }

        // Dispatch the login without account action if we have a good user, without an account and we aren't going to log them out
        if (user && !shouldLogoutUser && !accountExists) {
          await store.dispatch(loginWithoutAccountAction(user) as any);
        }
      } // end check for sso mode === yes

      // perform logic if in sso mode or not
      if (store.getState().auth.isLoggedIn) {
        try {
          // Change organization by URL param, if necessary
          // await store.dispatch(loginViaApi(user) as any);
          const redirectOrgId = parseInt(new URLSearchParams(window.location.search).get('redirectOrgId'));
          if (redirectOrgId && store.getState().auth.accessOrgs.some((accessOrg) => accessOrg.id === redirectOrgId)) {
            const newOrg = await store.dispatch(getOrganization(redirectOrgId) as any);
            if (newOrg) {
              await store.dispatch({ type: REDUX_ACTIONS.CHANGE_ORG, organization: newOrg });
            }
          }

          await store.dispatch(reloadMe() as any);

          if (store.getState().auth?.orgUser && store.getState().auth?.organization) {
            setUser(store.getState().auth.orgUser.id, {
              organization: store.getState().auth.organization,
              orgUser: store.getState().auth.orgUser,
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }

      setLoaded(true);
      store.dispatch({ type: REDUX_ACTIONS.UPDATE_FIREBASE_USER, user, token: await user?.getIdToken() });
    });
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={<Loader type="main" />} persistor={persistor}>
        <ConnectedRouter history={history}>{loaded ? <MainApp /> : <Loader type="main" />}</ConnectedRouter>
      </PersistGate>
      {Configs.GoogleTagManager.Key && (IS_PROD_ENV || IS_STAGING_ENV || IS_DEV_ENV) ? (
        <ReactHelmet>
          <script type="text/javascript">
            {`
              <!-- Google Tag Manager -->
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${Configs.GoogleTagManager.Key}');
              <!-- End Google Tag Manager -->
            `}
          </script>
        </ReactHelmet>
      ) : null}
      <ReactHelmet>
        <script type="text/javascript" async defer src="https://widget.cloudinary.com/v2.0/global/all.js" />
      </ReactHelmet>
    </Provider>
  );
}

export default App;
